import { useCallback, useState } from 'react';
import { InfiniteData, QueryClient, QueryKey, useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react-native';
import { FeedPaper } from './useProjectFeed';
import { FeedSearchPage } from './useProjectFeedSearch';
import {
    DownvoteReason,
    FeedbackVote,
    matchesFeedbackFilter,
    ProjectFeedFeedbackFilter,
    ProjectPaperFeedback,
} from '../projectPaperFeedback';
import { supabase, toError } from '../supabase';
import { msg } from '@lingui/core/macro';
import type { LocalizedMessage } from '../i18n/messages';

type FeedQueryPage = {
    papers: FeedPaper[];
    page: number;
};

type FeedInfiniteData = InfiniteData<FeedQueryPage, number>;
type FeedSearchInfiniteData = InfiniteData<FeedSearchPage, number>;

type FeedQueryKey = [
    'project-feed',
    string | undefined,
    unknown,
    ProjectFeedFeedbackFilter,
    number | undefined
];

type FeedInfiniteQueryKey = [
    'project-feed-infinite',
    string | undefined,
    unknown,
    ProjectFeedFeedbackFilter
];

type FeedCountQueryKey = [
    'project-feed-count',
    string | undefined,
    unknown,
    ProjectFeedFeedbackFilter
];

type FeedSearchQueryKey = [
    'project-feed-search',
    string | undefined,
    string,
    ProjectFeedFeedbackFilter,
    boolean | null
];

interface QuerySnapshot {
    queryKey: QueryKey;
    data: unknown;
}

interface UseProjectPaperFeedbackOptions {
    projectId?: string;
    surface?: string;
}

function updateFeedPaperList(
    papers: FeedPaper[],
    projectPaperId: string,
    nextFeedback: ProjectPaperFeedback | null,
    feedbackFilter: ProjectFeedFeedbackFilter
) {
    return papers
        .map((paper) => (
            paper.id === projectPaperId
                ? { ...paper, project_paper_feedback: nextFeedback }
                : paper
        ))
        .filter((paper) => matchesFeedbackFilter(paper.project_paper_feedback, feedbackFilter));
}

function collectSnapshots(queryClient: QueryClient, projectId: string) {
    return [
        ...queryClient.getQueryCache().findAll({ queryKey: ['project-feed', projectId] }),
        ...queryClient.getQueryCache().findAll({ queryKey: ['project-feed-infinite', projectId] }),
        ...queryClient.getQueryCache().findAll({ queryKey: ['project-feed-count', projectId] }),
        ...queryClient.getQueryCache().findAll({ queryKey: ['project-feed-search', projectId] }),
    ].map((query) => ({
        queryKey: query.queryKey,
        data: query.state.data,
    })) as QuerySnapshot[];
}

function restoreSnapshots(queryClient: QueryClient, snapshots: QuerySnapshot[]) {
    snapshots.forEach(({ queryKey, data }) => {
        queryClient.setQueryData(queryKey, data);
    });
}

function applyOptimisticFeedback(
    queryClient: QueryClient,
    projectId: string,
    projectPaperId: string,
    previousFeedback: ProjectPaperFeedback | null,
    nextFeedback: ProjectPaperFeedback | null
) {
    queryClient.getQueryCache().findAll({ queryKey: ['project-feed', projectId] }).forEach((query) => {
        const [, , , feedbackFilter] = query.queryKey as FeedQueryKey;
        queryClient.setQueryData(query.queryKey, (oldData?: FeedPaper[]) => {
            if (!oldData) {
                return oldData;
            }

            return updateFeedPaperList(oldData, projectPaperId, nextFeedback, feedbackFilter);
        });
    });

    queryClient.getQueryCache().findAll({ queryKey: ['project-feed-infinite', projectId] }).forEach((query) => {
        const [, , , feedbackFilter] = query.queryKey as FeedInfiniteQueryKey;
        queryClient.setQueryData(query.queryKey, (oldData?: FeedInfiniteData) => {
            if (!oldData) {
                return oldData;
            }

            return {
                ...oldData,
                pages: oldData.pages.map((page) => ({
                    ...page,
                    papers: updateFeedPaperList(page.papers, projectPaperId, nextFeedback, feedbackFilter),
                })),
            };
        });
    });

    queryClient.getQueryCache().findAll({ queryKey: ['project-feed-count', projectId] }).forEach((query) => {
        const [, , , feedbackFilter] = query.queryKey as FeedCountQueryKey;
        queryClient.setQueryData(query.queryKey, (oldData?: number) => {
            if (typeof oldData !== 'number') {
                return oldData;
            }

            const matchedBefore = matchesFeedbackFilter(previousFeedback, feedbackFilter);
            const matchesAfter = matchesFeedbackFilter(nextFeedback, feedbackFilter);
            const delta = Number(matchesAfter) - Number(matchedBefore);

            return Math.max(0, oldData + delta);
        });
    });

    queryClient.getQueryCache().findAll({ queryKey: ['project-feed-search', projectId] }).forEach((query) => {
        const [, , , feedbackFilter] = query.queryKey as FeedSearchQueryKey;
        queryClient.setQueryData(query.queryKey, (oldData?: FeedSearchInfiniteData) => {
            if (!oldData) {
                return oldData;
            }

            return {
                ...oldData,
                pages: oldData.pages.map((page) => ({
                    ...page,
                    papers: updateFeedPaperList(page.papers, projectPaperId, nextFeedback, feedbackFilter),
                })),
            };
        });
    });
}

async function invalidateFeedQueries(queryClient: QueryClient, projectId: string) {
    await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['project-feed', projectId] }),
        queryClient.invalidateQueries({ queryKey: ['project-feed-infinite', projectId] }),
        queryClient.invalidateQueries({ queryKey: ['project-feed-count', projectId] }),
        queryClient.invalidateQueries({ queryKey: ['project-feed-search', projectId] }),
    ]);
}

async function requireFeedbackSession() {
    const { data: { session }, error } = await supabase.auth.getSession();

    if (error) {
        throw toError(error);
    }

    if (!session?.user?.id) {
        throw new Error('You need to sign in again before saving feedback.');
    }

    return session;
}

export function useProjectPaperFeedback({
    projectId,
    surface = 'feed',
}: UseProjectPaperFeedbackOptions) {
    const queryClient = useQueryClient();
    const [pendingById, setPendingById] = useState<Record<string, boolean>>({});
    const [errorMessage, setErrorMessage] = useState<LocalizedMessage | null>(null);

    const markPending = useCallback((projectPaperId: string, isPending: boolean) => {
        setPendingById((current) => {
            if (isPending) {
                return { ...current, [projectPaperId]: true };
            }

            const next = { ...current };
            delete next[projectPaperId];
            return next;
        });
    }, []);

    const setFeedback = useCallback(async (
        projectPaperId: string,
        vote: FeedbackVote,
        reason: DownvoteReason = null,
        previousFeedback: ProjectPaperFeedback | null
    ) => {
        if (!projectId) {
            throw new Error('No project ID');
        }

        setErrorMessage(null);
        markPending(projectPaperId, true);

        const optimisticFeedback: ProjectPaperFeedback = {
            vote,
            downvote_reason: vote === 'downvote' ? reason : null,
            updated_at: new Date().toISOString(),
        };

        await Promise.all([
            queryClient.cancelQueries({ queryKey: ['project-feed', projectId] }),
            queryClient.cancelQueries({ queryKey: ['project-feed-infinite', projectId] }),
            queryClient.cancelQueries({ queryKey: ['project-feed-count', projectId] }),
        ]);

        const snapshots = collectSnapshots(queryClient, projectId);
        applyOptimisticFeedback(queryClient, projectId, projectPaperId, previousFeedback, optimisticFeedback);

        try {
            const session = await requireFeedbackSession();
            const { data, error } = await supabase
                .from('project_paper_feedback' as any)
                .upsert(
                    {
                        project_paper_id: projectPaperId,
                        vote,
                        downvote_reason: vote === 'downvote' ? reason : null,
                    } as any,
                    { onConflict: 'project_paper_id' }
                )
                .select('vote, downvote_reason, updated_at')
                .single();

            if (error) {
                throw toError(error);
            }

            const persistedFeedback = data as unknown as ProjectPaperFeedback;
            applyOptimisticFeedback(queryClient, projectId, projectPaperId, optimisticFeedback, persistedFeedback);

            const { error: eventError } = await supabase
                .from('project_signal_event' as any)
                .insert({
                    project_paper_id: projectPaperId,
                    user_id: session.user.id,
                    event_type: 'feedback_set',
                    vote,
                    downvote_reason: vote === 'downvote' ? reason : null,
                    source: 'paper_card',
                    context: {
                        surface,
                        reason_picker_opened: vote === 'downvote' && reason === null,
                    },
                } as any);

            if (eventError) {
                const loggedError = toError(eventError);
                Sentry.captureException(loggedError, {
                    extra: { projectId, projectPaperId, vote, reason, surface },
                });
                console.warn('[useProjectPaperFeedback] Feedback event log failed:', loggedError);
            }

            await invalidateFeedQueries(queryClient, projectId);
        } catch (error: any) {
            restoreSnapshots(queryClient, snapshots);
            console.error('[useProjectPaperFeedback] Could not save feedback:', error);
            setErrorMessage(msg({ id: 'feedback.saveFailure', message: 'Could not save feedback. Please try again.' }));
            throw error;
        } finally {
            markPending(projectPaperId, false);
        }
    }, [markPending, projectId, queryClient, surface]);

    const clearFeedback = useCallback(async (
        projectPaperId: string,
        previousFeedback: ProjectPaperFeedback | null
    ) => {
        if (!projectId) {
            throw new Error('No project ID');
        }

        setErrorMessage(null);
        markPending(projectPaperId, true);

        await Promise.all([
            queryClient.cancelQueries({ queryKey: ['project-feed', projectId] }),
            queryClient.cancelQueries({ queryKey: ['project-feed-infinite', projectId] }),
            queryClient.cancelQueries({ queryKey: ['project-feed-count', projectId] }),
        ]);

        const snapshots = collectSnapshots(queryClient, projectId);
        applyOptimisticFeedback(queryClient, projectId, projectPaperId, previousFeedback, null);

        try {
            const session = await requireFeedbackSession();
            const { error } = await supabase
                .from('project_paper_feedback' as any)
                .delete()
                .eq('project_paper_id', projectPaperId);

            if (error) {
                throw toError(error);
            }

            const { error: eventError } = await supabase
                .from('project_signal_event' as any)
                .insert({
                    project_paper_id: projectPaperId,
                    user_id: session.user.id,
                    event_type: 'feedback_cleared',
                    source: 'paper_card',
                    context: { surface },
                } as any);

            if (eventError) {
                const loggedError = toError(eventError);
                Sentry.captureException(loggedError, {
                    extra: { projectId, projectPaperId, surface },
                });
                console.warn('[useProjectPaperFeedback] Feedback clear event log failed:', loggedError);
            }

            await invalidateFeedQueries(queryClient, projectId);
        } catch (error: any) {
            restoreSnapshots(queryClient, snapshots);
            console.error('[useProjectPaperFeedback] Could not clear feedback:', error);
            setErrorMessage(msg({ id: 'feedback.clearFailure', message: 'Could not clear feedback. Please try again.' }));
            throw error;
        } finally {
            markPending(projectPaperId, false);
        }
    }, [markPending, projectId, queryClient, surface]);

    return {
        setFeedback,
        clearFeedback,
        isPending: (projectPaperId: string) => Boolean(pendingById[projectPaperId]),
        errorMessage,
        clearError: () => setErrorMessage(null),
    };
}
